import { mathFormat } from '@sg/utils/common';
import { localizeLabel } from '@sg/utils/translate';
import { chartAllColorsObj } from '@sg/utils/useChartOptions';
import InputNumberComponent from '../../../../../components/InputNumberComponent';
import {
  showRevenueTrendList,
  getFinancialsTableColumns,
  getFinancialsTableEditColumns,
} from '../../../../../pageConfig';

export interface IBuildLifecycleChartDataParams {
  data: any[];
  showChatsType: any;
  formatMessage: any;
  lifecycleUpdateTime: string;
  netRevenueForecast: any[];
  copySalesForecast: any[];
}

export const buildLifecycleChartData = ({
  data,
  showChatsType,
  formatMessage,
  lifecycleUpdateTime,
  netRevenueForecast,
  copySalesForecast,
}: IBuildLifecycleChartDataParams): Record<string, any> => {
  const chartData: any = {};
  if (showRevenueTrendList.length === 0) return chartData;

  // Determine the first forecast year based on gross_revenue_forecast or copy_sales_forecast
  let firstForecastYear: string | null = null;
  const forecastSource =
    netRevenueForecast?.length > 0
      ? netRevenueForecast
      : copySalesForecast?.length > 0
        ? copySalesForecast
        : null;

  if (forecastSource && forecastSource[0]?.year) {
    firstForecastYear = forecastSource[0].year.toString();
  }

  showRevenueTrendList.forEach((item) => {
    if (item === 'table') {
      // 为每行数据添加唯一的id（使用date作为id）
      const dataWithId = data.map((row: any) => ({
        ...row,
        id: row.date || `row-${Math.random()}`,
      }));

      chartData[item] = {
        data: dataWithId,
        columns: getFinancialsTableColumns(formatMessage),
        editColumns: getFinancialsTableEditColumns(
          formatMessage,
          InputNumberComponent,
          lifecycleUpdateTime
            ? parseInt(lifecycleUpdateTime.split('-')[0], 10)
            : undefined,
        ),
      };
      return;
    }

    const xAxisData: any = [];
    const datas: any = {};
    const revenueActualFlags: any = []; // Store revenue_is_actual flags for each data point
    const costActualFlags: any = []; // Store cost_is_actual flags for each data point

    data.forEach((element: any) => {
      xAxisData.push(element.date);
      revenueActualFlags.push(element.revenue_is_actual); // Store the flag
      costActualFlags.push(element.cost_is_actual); // Store the flag

      Object.keys(element).forEach((key) => {
        if (key !== 'date' && key !== 'revenue_is_actual' && key !== 'cost_is_actual') { // Exclude flag fields from series
          if (datas[key]) {
            datas[key].push(element[key]);
          } else {
            datas[key] = [element[key]];
          }
        }
      });
    });

    // Define the order and grouping of series
    const costSeriesKeys = ['Dev Cost', 'Mkt Cost', 'Other Cost']; // Stacked bars
    const revenueKey = 'Revenue'; // Separate bar (not stacked)
    const profitKey = 'Cum. Profit / Break even'; // Line chart

    chartData[item] = {
      xAxisData,
      type: showChatsType?.value,
      seriesData: Object.keys(datas)
        .filter((key) => key !== 'Total Project Cost')
        .map((keys) => {
          const isCostSeries = costSeriesKeys.includes(keys);
          const isRevenue = keys === revenueKey;
          const isProfit = keys === profitKey;

          const tdata: any = {
            name: localizeLabel(keys, formatMessage),
            barGap: '0%',
            tooltipWidth: '270px',
            barWidth: '25%',
            smooth: isProfit, // Only smooth for line chart
            data: datas[keys].map((items: any, i: number) => {
              // Use total_project_cost from API instead of calculating
              const totalProjectCost = datas['Total Project Cost']
                ? datas['Total Project Cost'][i]
                : 0;
              const isRevenueForecast = revenueActualFlags[i] === 0; // Check if revenue is forecast
              const isCostForecast = costActualFlags[i] === 0; // Check if cost is forecast
              const currentYear = xAxisData[i];

              // Calculate forecast year suffix (Y1, Y2, etc.) if this year is at or after firstForecastYear
              let forecastYearSuffix = '';
              if (
                firstForecastYear &&
                parseInt(currentYear, 10) >= parseInt(firstForecastYear, 10)
              ) {
                const yearDiff =
                  parseInt(currentYear, 10) - parseInt(firstForecastYear, 10) + 1;
                forecastYearSuffix = `_Y${yearDiff} Forecast`;
              }

              return {
                ...items,
                label: {
                  // Show labels for Net Revenue when view button is enabled
                  // Hide labels for cost series (will show total at top) and profit line
                  show: isRevenue ? showChatsType?.format : false,
                  position: items >= 0 ? 'top' : 'bottom', // Top for positive, bottom for negative
                  fontSize: 12,
                  fontWeight: 'normal',
                  color: '#525464',
                  opacity: 1, // Prevent label from inheriting itemStyle opacity
                  formatter: (params: any) => {
                    const value = params?.value ?? items;
                    if (value === null || value === undefined) return '';
                    return mathFormat(value);
                  },
                },
                value: items,
                tooltipWidth: '270px',
                tooltipTitleSuffix: forecastYearSuffix, // Add dynamic forecast year suffix
                // Not using totalFromVisible as other stacked bar chart because the total is not the sum of all cost series, but its another metrics return from backend (Total PRoject Cost)
                totalShowContent: mathFormat(totalProjectCost),
                totalTitleText: 'Total Project Cost',
                totalTitleMarker: `<span>
                  <svg width="8" height="8" viewBox="0 0 200 200">
                      <path d="M 100 100 L 100 0 A 100 100 0 0 1 186.6 150 Z" fill="#7F6FF1"/>
                      <path d="M 100 100 L 186.6 150 A 100 100 0 0 1 13.4 150 Z" fill="#6e7794ff"/>
                      <path d="M 100 100 L 13.4 150 A 100 100 0 0 1 100 0 Z" fill="#5EC8F1"/>
                  </svg>
                </span>`,
                headSplit: isRevenue, // Add separator above net revenue for tooltips
                // Apply styling: forecast opacity (30%) separately for revenue and cost series
                ...(!isProfit
                  ? {
                      itemStyle: {
                        ...((isRevenue && isRevenueForecast) ||
                        (isCostSeries && isCostForecast)
                          ? { opacity: 0.3 }
                          : {}),
                        ...(showChatsType?.value === 'bar' && isCostSeries
                          ? {
                              borderColor: '#fff',
                              borderWidth: 0.5,
                            }
                          : {}),
                      },
                    }
                  : {}),
              };
              // return items;
            }),
            color: chartAllColorsObj[keys] || '#5EC8F1',
            type: isProfit ? 'line' : showChatsType?.value, // Line for profit, bar for others
            stack: isCostSeries ? 'costs' : undefined, // Stack only cost series together
            showSymbol: isProfit ? showChatsType?.format : false, // Control line symbols with view button
            lineStyle: isProfit
              ? {
                  type: 'dashed', // Dashed line for profit
                }
              : undefined,
          };
          return tdata;
        }),
    };
  });

  return chartData;
};
